import { NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  NgbDropdownModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { ROUTE_NAMES } from '@constants';
import { CommunityProfileType, UserSubType } from '@models';
import {
  BreakpointService,
  buildNavigationItems,
  NavigationItem,
} from '@services';
import { ClickableDirective } from '@shared/directives/clickable.directive';
import {
  AppState,
  injectUserBean,
  setNavigationOpen,
  setNavigationRoute,
} from '@store';
import { RoomTreeComponent } from './room-tree.component';

@Component({
  selector: 'app-navigation',
  host: {
    role: 'navigation',
    '[class.expand]': 'navigation().open',
  },
  imports: [
    TranslatePipe,
    RoomTreeComponent,
    NgbTooltipModule,
    RouterLink,
    RouterLinkActive,
    NgbDropdownModule,
    ClickableDirective,
    NgTemplateOutlet,
  ],
  template: `
    <ul
      class="navigation-list d-md-flex flex-column list-unstyled p-0 m-0 position-relative"
      [class.expand]="navigation().open"
    >
      @for (navigationItem of navigationList; track navigationItem.link) {
        <li
          class="navigation-list-item"
          [routerLinkActive]="'active'"
          [class.active]="isNavLoaded && getIsStateMatch(navigationItem)"
        >
          @if (navigationItem.target) {
            <a
              class="navigation-link"
              [href]="navigationItem.link"
              [target]="navigationItem.target"
              rel="noopener"
              [attr.aria-label]="navigationItem.tooltip | translate"
            >
              <ng-container
                [ngTemplateOutlet]="navigationItemContent"
                [ngTemplateOutletContext]="{ $implicit: navigationItem }"
              />
            </a>
          } @else {
            <a
              class="navigation-link"
              [routerLink]="navigationItem.link"
              [attr.aria-label]="navigationItem.tooltip | translate"
              (click)="setNavigationRoute(navigationItem)"
            >
              <ng-container
                [ngTemplateOutlet]="navigationItemContent"
                [ngTemplateOutletContext]="{ $implicit: navigationItem }"
              />
            </a>
          }
          @if (navigationItem.route === ROUTE_NAMES.ROOMS) {
            <div class="navigation-submenu">
              @defer (when navigation().open) {
                @if (
                  (userBean().subtype === UserSubType.MEMBER ||
                    userBean().subtype === UserSubType.AZURE_AD) &&
                  community().communityProfileId !==
                    CommunityProfileType.SIGNING_ROOM &&
                  views().roomTreeView !== 3
                ) {
                  <app-room-tree />
                }
              }
            </div>
          }
        </li>
      }
    </ul>
    <ng-template #navigationItemContent let-navigationItem>
      <div
        class="navigation-item-icon"
        [ngbTooltip]="
          !navigation().open ? (navigationItem.tooltip | translate) : ''
        "
        placement="right"
      >
        <i
          class="agoicon agoicon-sm"
          [class]="
            getIsStateMatch(navigationItem)
              ? navigationItem.icon
              : navigationItem.iconOutline
          "
        ></i>
        @if (navigationItem.count) {
          <div
            class="badge text-bg-danger position-absolute"
            style="top: 5px; right: 5px"
          >
            {{ navigationItem.count }}
          </div>
        }
        @if (
          userBean().conversationNotifications &&
          navigationItem.route === ROUTE_NAMES.MESSAGES
        ) {
          <span class="notify">
            <span class="notify__dot"></span>
            <span class="notify__pulse"></span>
          </span>
        }
      </div>
      <div class="navigation-item-label">
        {{ navigationItem.tooltip | translate }}
      </div>
    </ng-template>
    <div
      appClickable
      class="navigation-toggle text-center mt-auto pb-2 d-none d-md-block"
      [attr.aria-expanded]="navigation().open"
      [attr.aria-label]="
        (navigation().open ? 'WEB_ACTIONS_COLLAPSE' : 'WEB_ACTIONS_EXPAND')
          | translate
      "
      (click)="toggleNavigation()"
    >
      @if (!navigation().open) {
        <i class="bi bi-chevron-double-right" aria-hidden="true"></i>
      }
      @if (navigation().open) {
        <i class="bi bi-chevron-double-left" aria-hidden="true"></i>
      }
    </div>
  `,
  styleUrl: './navigation.component.scss',
})
export class NavigationComponent implements OnInit {
  store = inject<Store<AppState>>(Store);
  router = inject(Router);
  breakpoint = inject(BreakpointService);

  navigationList: NavigationItem[] = [];
  userBean = injectUserBean();
  community = toSignal(
    this.store.select((state) => state.community),
    {
      requireSync: true,
    }
  );
  root = toSignal(
    this.store.select((state) => state.root),
    {
      requireSync: true,
    }
  );
  navigation = toSignal(
    this.store.select((state) => state.navigation),
    {
      requireSync: true,
    }
  );
  isNavLoaded = false;
  views = toSignal(
    this.store.select((state) => state.views),
    {
      requireSync: true,
    }
  );

  ROUTE_NAMES = ROUTE_NAMES;
  CommunityProfileType = CommunityProfileType;
  UserSubType = UserSubType;

  ngOnInit() {
    this.navigationList = buildNavigationItems({
      userBean: this.userBean(),
      community: this.community(),
      root: this.root(),
    });
    this.isNavLoaded = true;
  }

  toggleNavigation = () => {
    this.store.dispatch(
      setNavigationOpen({
        payload: !this.navigation().open,
      })
    );
  };

  closeNavigation = () => {
    this.store.dispatch(
      setNavigationOpen({
        payload: false,
      })
    );
  };

  setNavigationRoute = (navigationItem: NavigationItem) => {
    if (navigationItem.route) {
      if (
        this.breakpoint.below('md')() &&
        (navigationItem.link !== '/rooms' ||
          this.navigation().route === ROUTE_NAMES.ROOMS)
      ) {
        this.closeNavigation();
      }

      if (navigationItem.route !== this.navigation().route) {
        this.store.dispatch(
          setNavigationRoute({ payload: navigationItem.route })
        );
      }
    }
  };

  getIsStateMatch = (navigationItem: NavigationItem) =>
    this.router.url.includes(navigationItem.link);
}
